import moment from "moment";
import { useTranslation } from "react-i18next";
import { useCallback, useRef, useEffect, useState } from "react";
// import { Dropdown } from "antd";
// import { EllipsisOutlined, CopyOutlined } from "@ant-design/icons";
import { TableCell } from "../../../components/table/table-cell/table-cell";
import InfinityTable from "../../../components/infinity-table/infinity-table";
import encodeSvgWithColor from "../../../common/utils/encodeSvgWithColor";
import UserActivityStatus from "../../../components/user-activity-status/user-activity-status";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";

const TaskTable = ({
  data,
  onHandleRowClick,
  onChange,
  isLoading,
  hasMore,
  fetchData,
  onBulkDelete,
  sorting,
}) => {
  const { t } = useTranslation();
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const {
    selectedKeys,
    clearSelection,
    getSelectionColumn,
    setSelectedKeys,
  } = useBulkSelection(data, { rowKey: "id" });

  const handleBulkDeleteClick = useCallback(() => {
    if (!onBulkDelete || selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    onBulkDelete(selectedKeys, {
      onDone: ({ failedIds = [] } = {}) => {
        if (!failedIds.length) {
          clearSelection();
        } else {
          setSelectedKeys(failedIds);
        }

        setBulkDeleting(false);
      },
    });
  }, [
    onBulkDelete,
    selectedKeys,
    isBulkDeleting,
    clearSelection,
    setSelectedKeys,
  ]);

  const columns = [
    getSelectionColumn(),
    {
      title: t("task_management.table.task"),
      dataIndex: "title",
      render: (_, task) => {
        const { avatar, avatarColor, backgroundColor, title } = task;
        return (
          <TableCell
            img={avatar ? encodeSvgWithColor(avatar, avatarColor) : " "}
            imgStyle={{
              backgroundColor: backgroundColor,
              backgroundSize: "50%",
            }}
            title={title}
          />
        );
      },
      sorter: true,
      sortOrder: sorting?.sortField === "title"
        ? (sorting.sortOrder === "ASC" ? "ascend" : "descend")
        : null,
      key: "title",
      width: "25%",
    },
    {
      title: t("task_management.table.cohorts"),
      dataIndex: "cohorts",
      render: (_, task) => {
        const planNames = (task.cohorts || [])
          .map((p) => p.value)
          .sort((a, b) => a.localeCompare(b))
          .join(", ");

        return <TableCell text={planNames || "All cohorts"} />;
      },
      key: "cohorts",
      width: "18%",
      sorter: true,
      sortOrder: sorting?.sortField === "cohorts"
        ? (sorting.sortOrder === "ASC" ? "ascend" : "descend")
        : null,
    },
    {
      title: t("task_management.table.category"),
      dataIndex: "category",
      render: (_, task) => {
        return <TableCell text={task.Category?.name || "N/A"} />;
      },
      key: "category",
      width: "11%",
      sorter: true,
      sortOrder: sorting?.sortField === "category"
        ? (sorting.sortOrder === "ASC" ? "ascend" : "descend")
        : null,
    },
    {
      title: t("task_management.table.type"),
      dataIndex: "type",
      render: (_, task) => {
        return (
          <TableCell
            textStyle={{ textTransform: "capitalize" }}
            text={task.taskType || "N/A"}
          />
        );
      },
      key: "type",
      width: "11%",
      sorter: true,
      sortOrder: sorting?.sortField === "type"
        ? (sorting.sortOrder === "ASC" ? "ascend" : "descend")
        : null,
    },
    {
      title: t("task_management.table.status"),
      dataIndex: "statusSummary",
      render: (_, item) => (
        <UserActivityStatus
          style={{ whiteSpace: "nowrap" }}
          userStatus={item?.statusSummary}
        />
      ),
      key: "statusSummary",
      width: "11%",
    },
    {
      title: t("task_management.table.last_modified"),
      dataIndex: "updatedAt",
      render: (_, task) => {
        return (
          <TableCell
            textStyle={{ whiteSpace: "nowrap" }}
            text={
              `${moment(task.updatedAt)
                .utc()
                .format("DD MMM YYYY, HH:mm")}, UTC` || "N/A"
            }
          />
        );
      },
      sorter: true,
      sortOrder: sorting?.sortField === "updatedAt"
        ? (sorting.sortOrder === "ASC" ? "ascend" : "descend")
        : null,
      key: "updatedAt",
      width: "18%",
    }
  ];

  return (
    <InfinityTable
      rowTestID={(task) => `task-list-row-${task.id}`}
      data={data}
      fetchData={fetchData}
      hasMore={hasMore}
      tableColumns={columns}
      isLoading={isLoading}
      onHandleRowClick={onHandleRowClick}
      onChange={onChange}
      containerRef={tableRef}
      after={
        <BulkSelectionBar
          left={bulkLeft}
          count={selectedKeys.length}
          onClear={clearSelection}
          onDelete={handleBulkDeleteClick}
          loading={isBulkDeleting}
        />
      }
    />
  );
};

export default TaskTable;
